import React, { useEffect, useState, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const STATUS_COLORS = {
  TRIAL:     { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  ACTIVE:    { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  PAST_DUE:  { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
  GRACE:     { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  EXPIRED:   { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' },
  CANCELLED: { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' }
};

const fmtINR = (n) => {
  if (n == null) return '₹0';
  return '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const fmtDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.EXPIRED;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      fontSize: 11, fontWeight: 600, background: s.bg, color: s.color,
      border: `1px solid ${s.border}`, textTransform: 'uppercase', letterSpacing: 0.3
    }}>
      {status}
    </span>
  );
}

function SubscriptionList({ token, onViewDetail }) {
  const { isMobile } = useIsMobile();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const headers = () => ({
    Authorization: `Bearer ${token}`
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (search.trim()) params.set('chain_id', search.trim());
      params.set('page', String(page));
      params.set('limit', String(limit));
      const res = await fetch(`/api/v1/subscription/admin/subscriptions?${params}`, { headers: headers() });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) throw new Error('Server unavailable');
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to load');
      setRows(json.rows || json.subscriptions || []);
      setTotal(json.total || 0);
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, statusFilter, search, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const thStyle = {
    padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: 0.5, color: '#6b7280',
    borderBottom: '2px solid var(--theme-border, #e5e7eb)', whiteSpace: 'nowrap',
    background: 'var(--theme-surface, #f9fafb)'
  };
  const tdStyle = {
    padding: '10px 12px', fontSize: 13, borderBottom: '1px solid var(--theme-border, #e5e7eb)',
    color: 'var(--theme-text, #374151)', whiteSpace: 'nowrap'
  };

  return (
    <div style={{ padding: isMobile ? 12 : 24, maxWidth: 1400, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 16px', color: 'var(--theme-text, #1f2937)', fontSize: isMobile ? 18 : 22 }}>
        Subscriptions
      </h2>

      {/* Filter bar */}
      <div style={{
        display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center'
      }}>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          style={{
            padding: '7px 12px', borderRadius: 6, border: '1px solid var(--theme-border, #d1d5db)',
            fontSize: 13, background: 'var(--theme-surface, #fff)', color: 'var(--theme-text, #374151)',
            minWidth: 140
          }}
        >
          <option value="">All Statuses</option>
          {Object.keys(STATUS_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input
          placeholder="Search chain name..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{
            padding: '7px 12px', borderRadius: 6, border: '1px solid var(--theme-border, #d1d5db)',
            fontSize: 13, flex: 1, minWidth: 160, maxWidth: 300,
            background: 'var(--theme-surface, #fff)', color: 'var(--theme-text, #374151)'
          }}
        />
      </div>

      {error && (
        <div style={{
          padding: '10px 14px', marginBottom: 16, background: '#fef2f2',
          border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 13
        }}>
          {error}
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading...</div>}

      {!loading && (
        <>
          {/* Mobile: cards, Desktop: table */}
          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {rows.map((r) => (
                <div
                  key={r.subscription_id || r.id}
                  onClick={() => onViewDetail && onViewDetail(r.subscription_id || r.id)}
                  style={{
                    background: 'var(--theme-surface, #fff)',
                    border: '1px solid var(--theme-border, #e5e7eb)',
                    borderRadius: 8, padding: 14, cursor: 'pointer'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <strong style={{ fontSize: 14, color: 'var(--theme-text, #1f2937)' }}>
                      {r.chain_name || r.chain_id || '-'}
                    </strong>
                    <StatusBadge status={r.status} />
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                    {r.property_name || r.property_id || '-'} | {r.scope || '-'}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {r.plan_name || '-'} | {r.billing_period || '-'} | {fmtINR(r.amount)}
                  </div>
                </div>
              ))}
              {rows.length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>No subscriptions found.</div>
              )}
            </div>
          ) : (
            <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Chain</th>
                    <th style={thStyle}>Property</th>
                    <th style={thStyle}>Scope</th>
                    <th style={thStyle}>Plan</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Billing Period</th>
                    <th style={thStyle}>Trial End / Period End</th>
                    <th style={thStyle}>Amount</th>
                    <th style={thStyle}>Created</th>
                    <th style={thStyle}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.subscription_id || r.id}
                      onClick={() => onViewDetail && onViewDetail(r.subscription_id || r.id)}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--theme-hover, #f3f4f6)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = ''}
                    >
                      <td style={tdStyle}>{r.chain_name || r.chain_id || '-'}</td>
                      <td style={tdStyle}>{r.property_name || r.property_id || '-'}</td>
                      <td style={tdStyle}>{r.scope || '-'}</td>
                      <td style={tdStyle}>{r.plan_name || '-'}</td>
                      <td style={tdStyle}><StatusBadge status={r.status} /></td>
                      <td style={tdStyle}>{r.billing_period || '-'}</td>
                      <td style={tdStyle}>
                        {r.status === 'TRIAL' ? fmtDate(r.trial_end_date) : fmtDate(r.current_period_end)}
                      </td>
                      <td style={tdStyle}>{fmtINR(r.amount)}</td>
                      <td style={tdStyle}>{fmtDate(r.created_at)}</td>
                      <td style={tdStyle}>
                        <button
                          onClick={(e) => { e.stopPropagation(); onViewDetail && onViewDetail(r.subscription_id || r.id); }}
                          style={{
                            padding: '4px 12px', borderRadius: 4, border: '1px solid var(--theme-primary, #2563eb)',
                            background: 'transparent', color: 'var(--theme-primary, #2563eb)',
                            fontSize: 12, cursor: 'pointer', fontWeight: 500
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={10} style={{ ...tdStyle, textAlign: 'center', padding: 40, color: '#6b7280' }}>
                        No subscriptions found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginTop: 16, fontSize: 13, color: '#6b7280'
          }}>
            <span>Page {page} of {totalPages} ({total} total)</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{
                  padding: '6px 14px', borderRadius: 6,
                  border: '1px solid var(--theme-border, #d1d5db)',
                  background: 'var(--theme-surface, #fff)',
                  color: page <= 1 ? '#d1d5db' : 'var(--theme-text, #374151)',
                  cursor: page <= 1 ? 'default' : 'pointer', fontSize: 13
                }}
              >
                Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                style={{
                  padding: '6px 14px', borderRadius: 6,
                  border: '1px solid var(--theme-border, #d1d5db)',
                  background: 'var(--theme-surface, #fff)',
                  color: page >= totalPages ? '#d1d5db' : 'var(--theme-text, #374151)',
                  cursor: page >= totalPages ? 'default' : 'pointer', fontSize: 13
                }}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SubscriptionList;
